import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { usePipelines, useSavePipeline } from "@/api/crm";
import type { Pipeline, StageKind } from "@/api/types";
import { Alert, PageHeader, Spinner } from "@/components/ui";

interface Row { id?: string; name: string; probability: number; kind: StageKind }

function Editor({ pipeline }: { pipeline: Pipeline }) {
  const save = useSavePipeline(pipeline.id);
  const [name, setName] = useState(pipeline.name);
  const [rows, setRows] = useState<Row[]>(pipeline.stages.map(({ id, name, probability, kind }) => ({ id, name, probability, kind })));
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  useEffect(() => { setName(pipeline.name); setRows(pipeline.stages.map(({ id, name, probability, kind }) => ({ id, name, probability, kind }))); }, [pipeline]);

  const open = rows.filter((r) => r.kind === "open");
  const closed = rows.filter((r) => r.kind !== "open");
  const update = (i: number, patch: Partial<Row>) => setRows((all) => { const o = all.filter((r) => r.kind === "open"); o[i] = { ...o[i]!, ...patch }; return [...o, ...all.filter((r) => r.kind !== "open")]; });
  const moveRow = (i: number, dir: -1 | 1) => setRows((all) => { const o = all.filter((r) => r.kind === "open"); const j = i + dir; if (j < 0 || j >= o.length) return all; [o[i], o[j]] = [o[j]!, o[i]!]; return [...o, ...all.filter((r) => r.kind !== "open")]; });

  const submit = async () => {
    setMessage(null);
    try {
      await save.mutateAsync({ name, is_default: pipeline.is_default, stages: rows.map((r) => ({ ...r, name: r.name.trim() })) });
      setMessage({ tone: "ok", text: "Pipeline saved." });
    } catch (e) {
      setMessage({ tone: "danger", text: e instanceof ApiError ? (Object.values(e.fieldErrors())[0] ?? e.message) : "Could not save" });
    }
  };

  return (
    <section className="card space-y-4 p-5" aria-label={`Pipeline ${pipeline.name}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1"><label htmlFor={`pn-${pipeline.id}`} className="label">Pipeline name</label><input id={`pn-${pipeline.id}`} className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        {pipeline.is_default && <span className="rounded-full bg-brand-soft px-2 py-1 text-xs font-medium text-brand">Default</span>}
      </div>
      <ol className="space-y-2" aria-label="Open stages">
        {open.map((r, i) => (
          <li key={r.id ?? `new-${i}`} className="flex flex-wrap items-center gap-2">
            <span className="w-6 text-right text-xs text-muted">{i + 1}</span>
            <input className="input min-w-40 flex-1" aria-label={`Stage ${i + 1} name`} value={r.name} onChange={(e) => update(i, { name: e.target.value })} />
            <label className="flex items-center gap-1 text-xs text-muted"><input type="number" min={0} max={100} className="input w-20" aria-label={`Stage ${i + 1} probability`} value={r.probability} onChange={(e) => update(i, { probability: Number(e.target.value) })} />%</label>
            <button type="button" className="btn-ghost px-2" aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => moveRow(i, -1)}>↑</button>
            <button type="button" className="btn-ghost px-2" aria-label={`Move ${r.name} down`} disabled={i === open.length - 1} onClick={() => moveRow(i, 1)}>↓</button>
            <button type="button" className="btn-ghost px-2 text-danger" aria-label={`Remove ${r.name}`} disabled={open.length <= 1} onClick={() => setRows((all) => all.filter((x) => x !== r))}>✕</button>
          </li>
        ))}
      </ol>
      <button type="button" className="btn-ghost text-xs" onClick={() => setRows((all) => [...all.filter((r) => r.kind === "open"), { name: "New stage", probability: 50, kind: "open" }, ...all.filter((r) => r.kind !== "open")])}>Add stage</button>
      <p className="text-xs text-muted">Closing stages: {closed.map((r) => r.name).join(" and ")}. Every pipeline has exactly one won and one lost stage.</p>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <div className="flex justify-end"><button type="button" className="btn-primary" disabled={save.isPending} onClick={() => void submit()}>Save pipeline</button></div>
    </section>
  );
}

export function PipelinesPage() {
  const pipelines = usePipelines();
  return (
    <>
      <PageHeader title="Pipelines" subtitle="The stages every deal moves through. Probabilities drive the weighted forecast." />
      {pipelines.isLoading && <Spinner />}
      <div className="space-y-6">{pipelines.data?.map((p) => <Editor key={p.id} pipeline={p} />)}</div>
    </>
  );
}
